import {htmlToText, sanitizeHtml, textToHtml} from '@frontapp/editor-html';
import {chain, compact, uniqBy} from 'lodash';

import {randomString} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {
  ApplicationDraft,
  ApplicationDraftContent,
  ApplicationDraftId,
  ApplicationDraftReplyType,
  ApplicationDraftTemplate,
  ApplicationDraftUpdate,
  ApplicationMessage,
  ApplicationMessageList,
  ApplicationMessageRecipients,
  ApplicationMessageStatus,
} from '../../../applications/bridge/v2/types/bridgeTypesV2';
import {uploadAttachmentAsync} from '../../../helpers/attachmentHelpers';
import {getFileContentType} from '../../../helpers/fileHelpers';
import {
  serializeAttachmentForApplication,
  serializeChannelForApplication,
  serializeRecipientForApplication,
} from '../../../helpers/models/applicationSerializationHelpers';
import {buildMessagePatcherFactory, MessagePatch} from '../../../helpers/models/messagePatchHelpers';
import {ReplyTypesEnum} from '../../../helpers/models/recipientHelpers';
import {getRecipientParser} from '../../../helpers/models/recipientParsers';
import {
  AttachmentStatusesEnum,
  parseVisualContentType,
  UploadedAttachmentModel,
} from '../../../models/api/attachmentModel';
import {ChannelModel} from '../../../models/api/channelModel';
import {ContactModel, ContactSourcesEnum} from '../../../models/api/contactModel';
import {
  DraftMessageModel,
  MessageModel,
  MessageStatusesEnum,
  MessageVisualStatusesEnum,
  MessageVisualTypesEnum,
} from '../../../models/api/messageModel';
import {RecipientRolesEnum} from '../../../models/api/recipientModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {buildPublicId, tryParsePublicId} from '../../../models/applications/publicIdTypes';
import {buildPublicUid, tryParsePublicUid} from '../../../models/applications/publicUidTypes';
import {findMessageComposer} from '../../../services/composers';
import {ReplyLocationsEnum} from '../../../services/tracking';
import {AppState} from '../../appState';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {ResourceMap} from '../../appTypes';
import {ViewerPanelId} from '../../panels/panelStateBase';
import {fetchChannelByIdAsyncAction} from '../channels/channelsActions';
import {makeGetReplyChannels, makeGetSortedComposeChannels} from '../channels/channelsSelectors';
import {getCompanyUrl} from '../company/companySelectors';
import {makeGetLoadedDraftMessages} from '../conversations/conversationsSelectors';
import {getContacts, getConversations, getMessages} from '../dataSelectors';
import {composeMessageAction, replyToMessageAction} from '../messages/draftMessagesActions';
import {makeGetDraftChannel} from '../messages/messagesSelectors';
import {saveMessageAsyncAction} from '../messages/saveMessageActions';
import {getFindTeammate, getFindTeammateByEmail} from '../teammates/lazyTeammatesSelectors';
import {getUser, getUserId} from '../teammates/teammatesSelectors';
import {listLoadedMessageAction} from './applicationsMessagesLoadActions';

/*
 * Actions.
 */

export function applicationListConversationsMessagesAction(
  panelId: ViewerPanelId,
  conversationId: number,
): ThunkAction<ApplicationMessageList> {
  return (dispatch, getState) => {
    const messages = dispatch(listLoadedMessageAction(panelId, conversationId));
    return {
      nextPageToken: undefined,
      results: messages.map((m) => serializeMessageForApplication(getState(), m)),
    };
  };
}

export function applicationFetchDraftAction(
  conversationId: number,
  draftPublicUid: ApplicationDraftId,
): ThunkAction<ApplicationDraft | undefined> {
  return (_dispatch, getState) => {
    const draftUid = tryParsePublicUid(draftPublicUid);
    if (!draftUid) {
      throw new Error('Invalid ID.');
    }

    const conversations = getConversations(getState());
    const conversation = conversations[conversationId];
    if (!conversation) {
      throw new Error('The conversation for this context could not be found.');
    }

    const userId = getUserId(getState());
    const draftMessages = makeGetLoadedDraftMessages(() => conversation)(getState(), {});
    const draftMessage = draftMessages.find(
      (d) => d.uid === draftUid && d.authorId === userId && d.status === MessageStatusesEnum.DRAFT_COMPOSE,
    );

    if (!draftMessage) {
      return undefined;
    }

    return serializeDraftForApplication(getState(), draftMessage);
  };
}

export function applicationComposeMessageAsyncAction(
  draftTemplate: ApplicationDraftTemplate,
): ThunkAsyncAction<ApplicationDraft> {
  return async (dispatch, getState) => {
    const {channelId, subject, content} = draftTemplate;

    const specifiedChannelId = channelId && tryParsePublicId(channelId);
    const getComposeChannels = makeGetSortedComposeChannels({
      getSpecifiedChannelId: () => specifiedChannelId,
    });
    const composeChannels = getComposeChannels(getState(), {});

    // If a channel ID was requested, fail rather than use another channel.
    const channel = channelId ? composeChannels.find((c) => c.id === specifiedChannelId) : composeChannels[0];
    if (!channel) {
      const errorMessage = channelId
        ? `Unable to find the channel ${channelId}`
        : `No channels are available to compose a new message.`;

      throw new Error(errorMessage);
    }

    const recipients = parseRecipientsFromDraftTemplate(draftTemplate, channel.contactSource);
    const {trustedHtml, text} = formatContentFromDraftTemplate(content);

    const draft = dispatch(
      composeMessageAction({
        recipients,
        subject,
        channelId: channel.id,
        trustedHtml,
        text,
      }),
    );

    // Start saving the draft without waiting.
    dispatch(
      saveMessageAsyncAction(draft.uid, {
        patchMessage: buildMessagePatcherFactory()({}),
        getVersion: () => draft.version,
      }),
    );

    return serializeDraftForApplication(getState(), draft);
  };
}

const applicationDraftReplyTypesMap: {[key in ApplicationDraftReplyType]: ReplyTypesEnum} = {
  forward: ReplyTypesEnum.FORWARD,
  reply: ReplyTypesEnum.SINGLE,
  replyAll: ReplyTypesEnum.ALL,
};

export function applicationComposeReplyAction(
  panelId: ViewerPanelId,
  conversationId: number,
  draftTemplate: ApplicationDraftTemplate,
): ThunkAsyncAction<ApplicationDraft> {
  return async (dispatch, getState) => {
    const {channelId, subject, replyOptions, content, attachments} = draftTemplate;
    if (!replyOptions) {
      throw new Error('Unable to compose a reply without reply options.');
    }

    const originalMessageId = tryParsePublicId(replyOptions.originalMessageId);

    // Ensure the original message is available and loaded.
    const loadedMessages = dispatch(listLoadedMessageAction(panelId, conversationId));
    const message = loadedMessages.find((m) => m.id === originalMessageId);
    if (!message) {
      throw new Error(`Unable to find the message ${replyOptions.originalMessageId}`);
    }

    // Find the channel to use to reply.
    const getReplyChannels = makeGetReplyChannels({getMessage: () => message});
    const replyChannels = getReplyChannels(getState(), {});
    const replyChannelId = channelId && tryParsePublicId(channelId);

    // If a channel ID was requested, fail rather than use another channel.
    const channel = channelId ? replyChannels.find((c) => c.id === replyChannelId) : replyChannels[0];
    if (!channel) {
      const errorMessage = channelId
        ? `Unable to find the channel ${channelId}`
        : `No channels are available to reply to the message ${replyOptions.originalMessageId}`;

      throw new Error(errorMessage);
    }

    const files = await uploadAttachmentsAsync(getCompanyUrl(getState()), attachments);
    const recipients = parseRecipientsFromDraftTemplate(draftTemplate, channel.contactSource);
    const {trustedHtml, text} = formatContentFromDraftTemplate(content);
    const replyType = applicationDraftReplyTypesMap[replyOptions.type];

    const draft = dispatch(
      replyToMessageAction({
        panelId,
        replyToUid: message.uid,
        conversationId,
        replyType,
        location: ReplyLocationsEnum.PLUGIN_API,
        overrides: {
          subject,
          recipients,
          trustedHtml,
          text,
          attachments: files,
        },
      }),
    );

    // Start saving the draft without waiting.
    dispatch(
      saveMessageAsyncAction(draft.uid, {
        patchMessage: buildMessagePatcherFactory()({}),
        getVersion: () => draft.version,
      }),
    );

    return serializeDraftForApplication(getState(), draft);
  };
}

export function applicationUpdateDraftAction(
  draftPublicUid: ApplicationDraftId,
  update: ApplicationDraftUpdate,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const messages = getMessages(getState());

    const draftUid = tryParsePublicUid(draftPublicUid);
    const draft = draftUid && messages[draftUid];
    if (!draft || draft.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
      throw new Error(`Unable to find a draft with the ID ${draftUid}`);
    }

    const composer = findMessageComposer(draft.uid);
    if (!composer) {
      throw new Error('The user does not have a composer open to edit this message.');
    }

    const channel = await dispatch(fetchChannelByIdAsyncAction(draft.fromChannelId));
    if (!channel) {
      throw new Error('The channel for the draft with this id could not be found.');
    }

    const {updateMode, subject, content} = update;
    const shouldReplace = updateMode === 'replace';

    // Handle any message updates, which we explicitly check certain fields instead of taking in the entire update.
    const {newRecipients, removedAttachmentCids, addedAttachments} = await dispatch(
      computeSaveMessagesUpdatesAsyncAction(update, channel, draft),
    );

    // Update HTML.
    const {trustedHtml} = formatContentFromDraftTemplate(content);
    if (trustedHtml) {
      if (shouldReplace) {
        composer.setHtml(trustedHtml);
      } else {
        composer.insertHtml(trustedHtml);
      }
    }

    // Update Subject.
    if (subject) {
      if (shouldReplace) {
        composer.setSubject(subject);
      } else {
        composer.insertSubject(subject);
      }
    }

    if (newRecipients || removedAttachmentCids || addedAttachments) {
      composer.saveMessage({
        newRecipients,
        removedAttachmentCids,
        addedAttachments,
      });
    }
  };
}

/*
 * Helpers.
 */

function computeSaveMessagesUpdatesAsyncAction(
  update: ApplicationDraftUpdate,
  channel: ChannelModel,
  draft: DraftMessageModel,
): ThunkAsyncAction<MessagePatch> {
  return async (_dispatch, getState) => {
    const {updateMode, attachments} = update;
    const shouldReplace = updateMode === 'replace';
    const contacts = getContacts(getState());

    const recipients = parseRecipientsFromDraftTemplate(update, channel.contactSource);
    const currentMessageAttachments = draft.attachments.map((attachment) => attachment.cid);
    const files = await uploadAttachmentsAsync(getCompanyUrl(getState()), attachments);

    return {
      newRecipients: shouldReplace ? recipients : mergeReplyRecipients(contacts, draft, recipients),
      removedAttachmentCids: shouldReplace ? currentMessageAttachments : undefined,
      addedAttachments: files,
    };
  };
}

async function uploadAttachmentsAsync(
  companyUrl: string,
  attachments?: ReadonlyArray<File>,
): Promise<ReadonlyArray<UploadedAttachmentModel> | undefined> {
  if (!attachments || attachments.length === 0) {
    return undefined;
  }

  const uploadedFiles: ReadonlyArray<UploadedAttachmentModel> = await Promise.all(
    attachments.map(async (file) => {
      try {
        // We need to read the file and reconstruct it as we can get missing information from the iframe.
        // Otherwise the browser can throw an error that is very crptic since that file potentially has an invalid file descriptor.
        const fileData = await readFileAsync(file);
        const serverAttachment = await uploadAttachmentAsync(
          companyUrl,
          new File([fileData], file.name, {type: file.type}),
        );
        const contentType = getFileContentType(file);

        const uploadedAttachment: UploadedAttachmentModel = {
          ...serverAttachment,
          name: file.name,
          linkId: undefined,
          giphyId: undefined,
          hasThumbnail: false,
          attachedAt: Date.now(),
          contentType,
          visualContentType: parseVisualContentType(contentType),
          cid: randomString(),
          isInline: false,
          status: AttachmentStatusesEnum.UPLOADED,
        };
        return uploadedAttachment;
      } catch (error) {
        throw new Error(`Failed to upload "${file.name}". Reason: ${error.message}`);
      }
    }),
  );
  return uploadedFiles;
}

function readFileAsync(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      resolve(reader.result as ArrayBuffer);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

function parseRecipientsFromDraftTemplate(
  draftTemplate: ApplicationDraftTemplate,
  source: ContactSourcesEnum,
) {
  const parser = getRecipientParser(source);
  if (!parser) {
    return undefined;
  }

  const {to, cc, bcc} = draftTemplate;
  if (!to && !cc && !bcc) {
    return undefined;
  }

  return compact([
    ...(to || []).map((r) => parser(RecipientRolesEnum.TO, r)),
    ...(cc || []).map((r) => parser(RecipientRolesEnum.CC, r)),
    ...(bcc || []).map((r) => parser(RecipientRolesEnum.BCC, r)),
  ]);
}

function formatContentFromDraftTemplate(content: ApplicationDraftContent | undefined) {
  if (!content) {
    return {trustedHtml: undefined, text: undefined};
  }

  const {body, type} = content;
  const trustedHtml = type === 'html' ? sanitizeHtml(body) : textToHtml(body);
  const text = type === 'html' ? htmlToText(body) : body;

  return {trustedHtml, text};
}

function mergeReplyRecipients(
  contacts: ResourceMap<ContactModel>,
  draft: DraftMessageModel,
  newRecipients: ReadonlyArray<VisualRecipientModel> | undefined,
): ReadonlyArray<VisualRecipientModel> | undefined {
  if (!newRecipients) {
    return undefined;
  }

  const draftRecipients = chain(draft.recipients)
    .map((r) => {
      const {contactUid, role, name} = r;
      const contact = contacts[contactUid];
      return contact && {role, contact, name, namespace: 'global'};
    })
    .compact()
    .value();

  return uniqBy([...draftRecipients, ...newRecipients], (r) => `${r.role}-${r.contact.uid}`);
}

/*
 * Serializers.
 */

function serializeMessageForApplication(state: AppState, message: MessageModel): ApplicationMessage {
  const partialMessage: ApplicationMessage = {
    id: buildPublicId('message', message.id),
    ...serializeMessageRecipientsForApplication(state, message),
    subject: message.subject,
    date: new Date(message.sentAt),
    status: serializeMessageStatusForApplication(message),
    content: undefined,
  };

  if (message.visualStatus === MessageVisualStatusesEnum.PARTIAL) {
    return partialMessage;
  }

  return {
    ...partialMessage,
    content: {
      body: message.body || message.text,
      type: message.visualType === MessageVisualTypesEnum.EMAIL ? 'html' : 'text',
      attachments: message.attachments.map(serializeAttachmentForApplication),
    },
  };
}

function serializeDraftForApplication(state: AppState, draft: DraftMessageModel): ApplicationDraft {
  // Find the channel for this draft.
  const getDraftChannel = makeGetDraftChannel(() => draft);
  const channel = getDraftChannel(state, {});

  // Build the recipient arrays.
  const {to, cc, bcc} = serializeMessageRecipientsForApplication(state, draft);

  // Serialize the draft.
  return {
    id: buildPublicUid('draft', draft.uid),
    channel: serializeChannelForApplication(state.data, channel),
    to,
    cc,
    bcc,
    subject: draft.subject,
    content: {
      body: draft.body || draft.text,
      type: draft.visualType === MessageVisualTypesEnum.EMAIL ? 'html' : 'text',
    },
  };
}

function serializeMessageRecipientsForApplication(
  state: AppState,
  message: MessageModel,
): ApplicationMessageRecipients {
  const conversation = getConversations(state)[message.conversationId];
  const teammate = getUser(state);
  const namespace = conversation?.namespace || teammate.namespace; // Default to the teammate's namespace.

  const baseResult: ApplicationMessageRecipients = {
    from: {handle: 'no-recipient@example.com', name: undefined, contact: undefined, type: 'email'},
    replyTo: undefined,
    to: [],
    cc: undefined,
    bcc: undefined,
  };

  return message.recipients.reduce((memo, recipient) => {
    // Serialize the recipient.
    const findTeammate = getFindTeammate(state);
    const findTeammateByEmail = getFindTeammateByEmail(state);
    const data = {
      ...state.data,
      findTeammate,
      findTeammateByEmail,
    };
    const applicationRecipient = serializeRecipientForApplication(data, recipient, namespace);

    // Find where to add it.
    switch (recipient.role) {
      case RecipientRolesEnum.FROM:
        return {...memo, from: applicationRecipient};

      case RecipientRolesEnum.REPLY_TO:
        return {...memo, replyTo: applicationRecipient};

      case RecipientRolesEnum.TO:
        return {...memo, to: [...memo.to, applicationRecipient]};

      case RecipientRolesEnum.CC:
        return {...memo, cc: [...(memo.cc || []), applicationRecipient]};

      case RecipientRolesEnum.BCC:
        return {...memo, bcc: [...(memo.bcc || []), applicationRecipient]};

      default:
        return memo;
    }
  }, baseResult);
}

function serializeMessageStatusForApplication(message: MessageModel): ApplicationMessageStatus {
  switch (message.status) {
    case MessageStatusesEnum.SENT:
    case MessageStatusesEnum.IMPORTED:
      return message.isInbound ? 'inbound' : 'outbound';

    default:
      throw new Error(`Unable to get the message status ${message.status}`);
  }
}
